import React, { useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase/firebase-config';
import { useUser } from '../../context/UserContext';
import User from '../../models/User';
import {
  DELETION_GRACE_PERIOD_DAYS,
  formatLifecycleDate,
  isAccountActive,
  isAccountPendingDeletion,
} from '../../platform/accountLifecycle';
import {
  cancelAccountDeletion,
  deactivateAccount,
  requestAccountDeletion,
} from '../../services/accountLifecycleService';
import './AccountSettings.css';

function AccountSettings({ showHeading = true }) {
  const { user, setUser } = useUser();
  const [busyAction, setBusyAction] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  if (!user) {
    return null;
  }

  const pendingDeletion = isAccountPendingDeletion(user);
  const activeAccount = isAccountActive(user);
  const scheduledDeletionLabel = formatLifecycleDate(user.scheduledDeletionAt);
  const deleteConfirmationPhrase = 'IZBRIŠI MOJ RAČUN';
  const canConfirmDelete = deleteConfirmation.trim() === deleteConfirmationPhrase;

  const applyLifecycleUpdate = (lifecycleFields) => {
    setUser(new User({ ...user, ...lifecycleFields }));
  };

  const runAction = async (actionName, actionFn, successMessage) => {
    setBusyAction(actionName);
    setError('');
    setSuccess('');

    try {
      const lifecycle = await actionFn(user);
      applyLifecycleUpdate(lifecycle);
      setSuccess(successMessage);
      return true;
    } catch (actionError) {
      setError(actionError.message || 'Dejanje ni uspelo.');
      return false;
    } finally {
      setBusyAction('');
    }
  };

  const handleDeactivate = async () => {
    const deactivated = await runAction(
      'deactivate',
      deactivateAccount,
      'Račun je bil deaktiviran.',
    );
    setShowDeactivateModal(false);
    if (!deactivated) {
      return;
    }
    await signOut(auth);
    setUser(null);
  };

  const handleRequestDeletion = async () => {
    await runAction(
      'delete',
      requestAccountDeletion,
      'Zahteva za izbris računa je bila shranjena.',
    );
    setShowDeleteModal(false);
    setDeleteConfirmation('');
  };

  const handleCancelDeletion = async () => {
    await runAction(
      'cancelDeletion',
      cancelAccountDeletion,
      'Izbris računa je bil preklican.',
    );
  };

  return (
    <section className="account-settings">
      {showHeading && (
        <>
          <hr className="profile-page__divider" />
          <h2 className="h4 mb-3">Račun</h2>
        </>
      )}

      <div className="platform-form-card account-settings__card">
        <div className="platform-form-card__body">
          {pendingDeletion && (
            <Alert variant="warning" className="account-settings__pending-alert">
              <Alert.Heading as="div" className="h6 mb-2">
                Načrtovan izbris računa
              </Alert.Heading>
              <p className="mb-2">
                Vaš račun bo trajno izbrisan
                {scheduledDeletionLabel ? ` ${scheduledDeletionLabel}` : ''}.
              </p>
              <p className="mb-3 text-muted small">
                Do izbrisa imate {DELETION_GRACE_PERIOD_DAYS} dni za preklic. Prijava ne prekliče izbrisa
                in ne podaljša roka.
              </p>
              <Button
                variant="outline-dark"
                size="sm"
                onClick={handleCancelDeletion}
                disabled={busyAction === 'cancelDeletion'}
              >
                {busyAction === 'cancelDeletion' ? 'Preklicevanje...' : 'Prekliči izbris'}
              </Button>
            </Alert>
          )}

          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {success && !error && (
            <Alert variant="success" className="mb-3">
              {success}
            </Alert>
          )}

          {activeAccount && (
            <div className="account-settings__sections">
              <div className="account-settings__section">
                <h3 className="account-settings__section-title">Deaktiviraj račun</h3>
                <p className="account-settings__section-text">
                  Začasno onemogočite dostop do portala. Vaši podatki, članstva in vsebina ostanejo nespremenjeni.
                </p>
                <div className="account-settings__section-action">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowDeactivateModal(true)}
                    disabled={Boolean(busyAction)}
                  >
                    Deaktiviraj račun
                  </Button>
                </div>
              </div>

              <div className="account-settings__section account-settings__section--danger">
                <h3 className="account-settings__section-title account-settings__section-title--danger">
                  Izbriši račun
                </h3>
                <p className="account-settings__section-text">
                  Trajni izbris računa. Po {DELETION_GRACE_PERIOD_DAYS} dneh od zahteve bo račun in osebni
                  podatki trajno izbrisani. Med tem obdobjem lahko izbris prekličete.
                </p>
                <div className="account-settings__section-action">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => setShowDeleteModal(true)}
                    disabled={Boolean(busyAction)}
                  >
                    Izbriši račun
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Deaktiviraj račun</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Ali ste prepričani, da želite deaktivirati račun? Dostop do portala bo onemogočen,
            dokler računa ponovno ne aktivirate ob prijavi.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeactivateModal(false)}>
            Prekliči
          </Button>
          <Button variant="primary" onClick={handleDeactivate} disabled={busyAction === 'deactivate'}>
            {busyAction === 'deactivate' ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deaktiviranje...
              </>
            ) : (
              'Deaktiviraj račun'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setDeleteConfirmation('');
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Izbriši račun</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Izbris je trajen. Po potrditvi imate {DELETION_GRACE_PERIOD_DAYS} dni, da izbris prekličete.
            Po tem roku izbrisa ni več mogoče razveljaviti.
          </p>
          <p className="mb-3 text-muted small">
            Med obdobjem čakanja lahko portal še naprej normalno uporabljate. Prijava ne prekliče izbrisa.
          </p>
          <Form.Group>
            <Form.Label>
              Za potrditev vnesite <strong>{deleteConfirmationPhrase}</strong>
            </Form.Label>
            <Form.Control
              value={deleteConfirmation}
              onChange={(event) => setDeleteConfirmation(event.target.value)}
              autoComplete="off"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmation('');
            }}
          >
            Prekliči
          </Button>
          <Button
            variant="danger"
            onClick={handleRequestDeletion}
            disabled={!canConfirmDelete || busyAction === 'delete'}
          >
            {busyAction === 'delete' ? 'Shranjevanje...' : 'Potrdi izbris'}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}

export default AccountSettings;
